import type { MatrixSelectionKey } from '../components/WorkJourneyMonthMatrixGrid';

import { toggleMatrixSelectionKey } from './getMatrixSelectionMeta';
import { isFutureMatrixCalendarDate } from './isFutureMatrixCalendarDate';

type MakeKey = (userId: string, date: string) => MatrixSelectionKey;

/** Keeps only calendar dates strictly after today (app default timezone). */
export const filterFutureMatrixIsoDates = (
  dates: readonly string[],
  nowMs: number = Date.now(),
): string[] => dates.filter(date => isFutureMatrixCalendarDate(date, nowMs));

/**
 * Inclusive range on one user row, limited to future days.
 * Past/today cells in the span are dropped so multi-select cannot mix eras.
 */
export const buildFutureMatrixRangeKeys = (
  userId: string,
  orderedDays: readonly string[],
  anchorDate: string,
  endDate: string,
  makeKey: MakeKey,
  nowMs: number = Date.now(),
): MatrixSelectionKey[] => {
  const start = Math.min(orderedDays.indexOf(anchorDate), orderedDays.indexOf(endDate));
  const end = Math.max(orderedDays.indexOf(anchorDate), orderedDays.indexOf(endDate));
  if (start < 0 || end < 0) {
    return [];
  }

  return filterFutureMatrixIsoDates(orderedDays.slice(start, end + 1), nowMs).map(date =>
    makeKey(userId, date),
  );
};

/**
 * ⌘/Ctrl toggle for multi-select: only future days participate.
 * Past/today clicks leave the selection unchanged.
 */
export const toggleFutureMatrixSelectionKey = (
  previous: Set<MatrixSelectionKey>,
  userId: string,
  date: string,
  makeKey: MakeKey,
  nowMs: number = Date.now(),
): Set<MatrixSelectionKey> => {
  if (!isFutureMatrixCalendarDate(date, nowMs)) {
    return previous;
  }
  return toggleMatrixSelectionKey(previous, userId, date, makeKey);
};
